import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { NestedMenuItem } from "mui-nested-menu"
import { Menu, MenuItem, TextField as MuiTextField } from "@mui/material"
import classNames from "classnames"

import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { colorTokens } from "@/components/colorTokens"

import "./sortSelector.scss"

type SortMenuOption = {
  key: string
  label: string
  children: any[]
  is3D?: boolean
  color?: string
}

type SortValue = {
  key: string
  order: "asc" | "desc"
}

export default function SortSelector({
  value,
  onChange,
  options,
  renderTrigger,
  menuClassName,
}: {
  value: SortValue
  onChange: (value: SortValue) => void
  options: SortMenuOption[]
  renderTrigger?: (handleOpen: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactNode
  menuClassName?: string
}) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const displayValue = useMemo(() => {
    for (const optionGroup of options) {
      if (optionGroup.is3D) {
        // 3D 메뉴: Metric → Order (Value 레이블 제외)
        for (const metric of optionGroup.children as any[]) {
          const orderItem = metric.children?.find(
            (c: any) => c.value?.key === value.key && c.value?.order === value.order,
          )
          if (orderItem) {
            return `${metric.label} / ${orderItem.label}`
          }
        }
      } else {
        // 2D 메뉴: Category → Order
        const child = optionGroup.children.find(
          (c: any) => c.value?.key === value.key && c.value?.order === value.order,
        )
        if (child) {
          return `${optionGroup.label} / ${child.label}`
        }
      }
    }

    return `${t("jersey")} / ${t("ascending")}`
  }, [value, options, t])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div className="sort-selector">
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <MuiTextField
          value={displayValue}
          onClick={handleOpen}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <span style={{ cursor: "pointer", display: "flex" }}>
                  <Icon
                    type="arrow-down"
                    color={colorTokens.field["ico-base"]}
                    className={`sort-icon ${anchorEl ? "open" : ""}`}
                    size={16}
                  />
                </span>
              ),
            },
          }}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        className={classNames("sort-selector-menu", menuClassName)}
        disableAutoFocus
        disableEnforceFocus>
        {options.map((option) =>
          option.is3D ? (
            // 3D 메뉴: Value → Metric → Order
            <NestedMenuItem
              key={option.key}
              label={option.label}
              parentMenuOpen
              delay={0}
              MenuProps={{
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                className: classNames("sort-selector-submenu", menuClassName),
              }}
              rightIcon={<Icon type="arrow-right" size={16} color={colors.neutral.grey[600]} />}>
              {(option.children as SortMenuOption[]).map((metric) => {
                return (
                  <NestedMenuItem
                    key={metric.key}
                    label={
                      (
                        <div className="metric-label-with-color">
                          {metric.color && (
                            <span
                              className="color-circle"
                              style={{ backgroundColor: metric.color }}
                            />
                          )}
                          <span>{metric.label}</span>
                        </div>
                      ) as unknown as string
                    }
                    parentMenuOpen
                    delay={0}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "top",
                        horizontal: "right",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      className: "sort-selector-third-menu",
                    }}
                    rightIcon={
                      <Icon type="arrow-right" size={16} color={colors.neutral.grey[600]} />
                    }>
                    {metric.children.map((order: any) => {
                      const isSelected =
                        order.value?.key === value.key && order.value?.order === value.order

                      return (
                        <MenuItem
                          key={order.key}
                          onClick={() => {
                            onChange(order.value)
                            handleClose()
                          }}
                          className="sort-selector-order-item">
                          <span className="order-label">{order.label}</span>
                          {isSelected && (
                            <Icon
                              type="check"
                              size={16}
                              color={colors.platinum[900]}
                              className="check-icon"
                            />
                          )}
                        </MenuItem>
                      )
                    })}
                  </NestedMenuItem>
                )
              })}
            </NestedMenuItem>
          ) : (
            // 2D 메뉴: Category → Order
            <NestedMenuItem
              key={option.key}
              label={option.label}
              parentMenuOpen
              delay={0}
              MenuProps={{
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                className: classNames("sort-selector-submenu", menuClassName),
              }}
              rightIcon={<Icon type="arrow-right" size={16} color={colors.neutral.grey[600]} />}>
              {option.children.map((child: any) => {
                const isSelected =
                  child.value?.key === value.key && child.value?.order === value.order

                return (
                  <MenuItem
                    key={child.key}
                    onClick={() => {
                      onChange(child.value)
                      handleClose()
                    }}
                    className="sort-selector-2d-item">
                    <span className="item-label">{child.label}</span>
                    {isSelected && (
                      <Icon
                        type="check"
                        size={16}
                        color={colors.platinum[900]}
                        className="check-icon"
                      />
                    )}
                  </MenuItem>
                )
              })}
            </NestedMenuItem>
          ),
        )}
      </Menu>
    </div>
  )
}
